import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine, Users, History, ShieldAlert, Search, X, CheckCircle2, AlertTriangle, Camera, RefreshCw, Loader2 } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { formatDateTime } from "@/lib/timezone";

const isAdminRole = (u) => ["admin", "super_admin"].includes(u?.role);

// short success "ding" via WebAudio so we don't need an asset file
const playDing = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.start();
    o.stop(ctx.currentTime + 0.4);
    setTimeout(() => ctx.close(), 600);
  } catch (err) {
    // Audio playback is best-effort — some browsers block until user gesture.
    console.debug("[pickup] ding suppressed:", err?.message);
  }
};

const formatTime12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hh = parseInt(h, 10);
  return `${hh % 12 || 12}:${m} ${hh >= 12 ? "PM" : "AM"}`;
};

export default function PickupPage({ user, onLogout }) {
  const [tab, setTab] = useState("scanner");
  const activeTabRef = useRef(null);

  // Auto-scroll active tab into view (mobile-first)
  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [tab]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="lg:ml-72 pb-24 lg:pb-8">
        <div className="px-4 sm:px-6 py-5 sm:py-6 max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert className="w-6 h-6 text-violet-600" />
            <h1 className="text-2xl font-bold text-slate-900 font-heading">Pickup Queue</h1>
          </div>

          {/* Tabs */}
          <div className="relative mb-5">
            <div className="flex gap-1 border-b border-slate-200 overflow-x-auto scrollbar-hide scroll-smooth snap-x">
              {[
                { key: "scanner", label: "Scanner", icon: ScanLine },
                { key: "queue", label: "Queue", icon: Users },
                { key: "manual", label: "Manual", icon: ShieldAlert, adminOnly: true },
                { key: "logs", label: "Logs", icon: History },
              ].filter((t) => !t.adminOnly || isAdminRole(user)).map((t) => {
                const Ico = t.icon;
                const isActive = tab === t.key;
                return (
                  <button
                    key={t.key}
                    ref={isActive ? activeTabRef : null}
                    data-testid={`pickup-tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`snap-start flex items-center gap-1.5 px-3 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 leading-5 transition shrink-0 ${
                      isActive ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <Ico className="w-3.5 h-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>
            {/* Right-edge fade hint */}
            <div className="pointer-events-none absolute top-0 right-0 h-full w-6 bg-gradient-to-l from-violet-50/80 via-white/60 to-transparent" />
          </div>

          {tab === "scanner" && <ScannerTab user={user} />}
          {tab === "queue" && <QueueTab user={user} />}
          {tab === "manual" && isAdminRole(user) && <ManualOverrideTab user={user} />}
          {tab === "logs" && <LogsTab user={user} />}
        </div>
      </div>
      <BottomNav user={user} onLogout={onLogout} />
    </div>
  );
}

// =================================================================
// Scanner Tab
// =================================================================

const ScannerTab = ({ user }) => {
  const [scannerOn, setScannerOn] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [manualToken, setManualToken] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);

  const stopScanner = useCallback(async () => {
    setScannerOn(false);
    if (html5QrRef.current) {
      try {
        await html5QrRef.current.stop();
        await html5QrRef.current.clear();
      } catch (err) {
        // Scanner may already be stopped or DOM unmounted — safe to ignore in prod, log in dev.
        console.debug("[pickup] scanner stop error:", err?.message);
      }
      html5QrRef.current = null;
    }
  }, []);

  const handleScannedToken = useCallback(async (text) => {
    await stopScanner();
    try {
      const res = await api.post("/pickup/scan", { token: text });
      setScanResult(res.data);
      setScanError(null);
    } catch (e) {
      setScanError(e.response?.data?.detail || "QR not recognised");
      setScanResult(null);
    }
  }, [stopScanner]);

  const startScanner = useCallback(async () => {
    setScanError(null);
    setScanResult(null);
    setScannerOn(true);
    try {
      // wait one paint so the div renders
      await new Promise((r) => setTimeout(r, 100));
      const inst = new Html5Qrcode("pickup-scanner-region");
      html5QrRef.current = inst;
      await inst.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => { handleScannedToken(decoded); },
        () => {/* ignore per-frame errors */}
      );
    } catch (e) {
      setScannerOn(false);
      setScanError("Cannot access camera. Use Manual Search instead.");
    }
  }, [handleScannedToken]);

  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const submitManualToken = (e) => {
    e?.preventDefault();
    const t = manualToken.trim();
    if (!t) return;
    handleScannedToken(t);
    setManualToken("");
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        {!scannerOn && !scanResult && (
          <div className="text-center py-8" data-testid="scanner-idle">
            <Camera className="w-12 h-12 text-violet-500 mx-auto mb-2" />
            <p className="text-sm text-slate-500 mb-4">Tap below to start the camera scanner.</p>
            <Button onClick={startScanner} data-testid="scanner-start" className="rounded-full bg-violet-600 hover:bg-violet-700 text-white">
              <ScanLine className="w-4 h-4 mr-1" /> Start Scanner
            </Button>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => setSearchOpen(true)} data-testid="scanner-manual-search">
                <Search className="w-3.5 h-3.5 mr-1" /> Manual Student Search
              </Button>
            </div>
            <form onSubmit={submitManualToken} className="mt-4 flex gap-2 max-w-sm mx-auto">
              <Input
                placeholder="Or paste QR token manually"
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                className="h-9 text-xs rounded-full"
                data-testid="scanner-manual-token-input"
              />
              <Button type="submit" size="sm" variant="outline" className="rounded-full">Verify</Button>
            </form>
          </div>
        )}
        {scannerOn && (
          <div>
            <div ref={scannerRef} id="pickup-scanner-region" className="rounded-xl overflow-hidden bg-black" data-testid="scanner-region" />
            <div className="flex justify-center mt-3">
              <Button variant="outline" onClick={stopScanner} className="rounded-full">
                <X className="w-4 h-4 mr-1" /> Stop
              </Button>
            </div>
          </div>
        )}
        {scanError && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 text-sm text-red-700 border border-red-200 flex items-center gap-2" data-testid="scanner-error">
            <AlertTriangle className="w-4 h-4 shrink-0" /> {scanError}
          </div>
        )}
      </div>

      {scanResult && (
        <VerificationCard
          data={scanResult}
          token={scanResult.qr_token}
          onCleared={() => { setScanResult(null); setScanError(null); }}
        />
      )}

      <ManualSearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onPicked={(payload) => { setSearchOpen(false); setScanResult(payload); }}
      />
    </div>
  );
};

const VerificationCard = ({ data, token, onCleared }) => {
  const student = data?.student || {};
  const session = data?.pending_session;
  const attendanceId = data?.attendance_id;
  const warning = data?.warning;
  const [confirming, setConfirming] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);

  const handover = async () => {
    if (!attendanceId) {
      toast.error(warning || "No pending pickup");
      return;
    }
    setConfirming(true);
    try {
      await api.post("/pickup/handover", {
        student_id: student.user_id,
        attendance_id: attendanceId,
        qr_token: token || data.student?.qr_token,
      });
      toast.success(`${student.name} handed over successfully.`);
      playDing();
      onCleared?.();
    } catch (e) {
      const msg = e.response?.data?.detail || "Handover failed";
      toast.error(msg);
      if (msg.toLowerCase().includes("already handed over")) {
        onCleared?.();
      }
    } finally { setConfirming(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-violet-200 p-4 space-y-3 shadow-sm" data-testid="verification-card">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => student.photo_data && setZoomOpen(true)}
          className={`w-16 h-16 rounded-2xl bg-violet-100 grid place-items-center overflow-hidden shrink-0 ${student.photo_data ? "cursor-zoom-in hover:ring-2 hover:ring-violet-400 transition-all" : ""}`}
          aria-label={student.photo_data ? "Enlarge photo" : "No photo"}
          data-testid="verify-photo-thumb"
        >
          {student.photo_data ? (
            <img src={`data:image/jpeg;base64,${student.photo_data}`} alt={student.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl font-bold text-violet-600">{(student.name || "?").charAt(0).toUpperCase()}</span>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-slate-900 truncate" data-testid="verify-name">{student.name}</p>
          <p className="text-xs text-violet-700 font-semibold">{student.display_id || "—"}</p>
          {student.parent_phone && <p className="text-[11px] text-slate-500 mt-0.5">Parent: {student.parent_phone}</p>}
        </div>
      </div>

      {session ? (
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-700">
          <p><strong>Batch:</strong> {session.batch_name || "—"}</p>
          <p><strong>Studio:</strong> {session.studio_name || "—"}</p>
          <p><strong>Time:</strong> {formatTime12(session.start_time)} – {formatTime12(session.end_time)}</p>
        </div>
      ) : (
        <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs p-3 flex gap-2" data-testid="verify-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {warning || "No pending pickup found."}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
        <Button variant="outline" onClick={onCleared} className="w-full sm:flex-1 rounded-full" data-testid="verify-cancel">
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
        <Button
          onClick={handover}
          disabled={confirming || !attendanceId}
          className="w-full sm:flex-1 rounded-full bg-green-600 hover:bg-green-700 text-white"
          data-testid="verify-handover"
        >
          {confirming ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
          Confirm Handed Over
        </Button>
      </div>

      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="max-w-2xl p-0 bg-transparent border-0 shadow-none">
          <DialogHeader className="sr-only">
            <DialogTitle>{student.name} — Photo</DialogTitle>
          </DialogHeader>
          {student.photo_data && (
            <img
              src={`data:image/jpeg;base64,${student.photo_data}`}
              alt={student.name}
              className="w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
              data-testid="verify-photo-zoomed"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =================================================================
// Manual Search dialog (for scan failure fallback / find a student)
// =================================================================

const ManualSearchDialog = ({ open, onOpenChange, onPicked }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setQ(""); setItems([]); return; }
    let cancelled = false;
    setLoading(true);
    api.get("/pickup/queue").then((r) => {
      if (!cancelled) setItems(r.data?.pending || []);
    }).catch((err) => {
      console.warn("[pickup] queue fetch failed", err);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const term = q.trim().toLowerCase();
    return items.filter((i) =>
      (i.student?.name || "").toLowerCase().includes(term) ||
      (i.student?.display_id || "").toLowerCase().includes(term) ||
      (i.student?.phone || "").includes(term) ||
      (i.student?.parent_phone || "").includes(term),
    );
  }, [items, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manual-search-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-heading">Manual Student Search</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name / ID / phone…" className="rounded-full h-9 text-sm" autoFocus />
          <div className="max-h-64 overflow-y-auto space-y-1">
            {loading ? (
              <p className="text-center text-xs text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-center text-xs text-slate-500 py-6">No matching pending pickups.</p>
            ) : filtered.map((i) => (
              <button
                key={i.attendance_id}
                onClick={() => onPicked({
                  student: i.student,
                  pending_session: i.session,
                  attendance_id: i.attendance_id,
                  warning: null,
                })}
                className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-violet-400 hover:bg-violet-50 transition"
                data-testid={`search-pick-${i.attendance_id}`}
              >
                <p className="text-sm font-semibold text-slate-900">{i.student?.name}</p>
                <p className="text-xs text-slate-500">{i.student?.display_id} · {i.session?.batch_name || "—"} · {formatTime12(i.session?.start_time)}</p>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =================================================================
// Queue Tab
// =================================================================

const QueueTab = ({ user }) => {
  const [data, setData] = useState({ pending: [], pending_count: 0, handed_over_count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [verify, setVerify] = useState(null);
  const [q, setQ] = useState("");
  const studioId = (typeof window !== "undefined" && localStorage.getItem("active_studio_id")) || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = studioId ? `/pickup/queue?studio_id=${studioId}` : "/pickup/queue";
    api.get(url).then((r) => {
      if (!cancelled) setData(r.data);
    }).catch((err) => {
      console.warn("[pickup] queue fetch failed", err);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, studioId]);

  const filtered = useMemo(() => {
    if (!q.trim()) return data.pending || [];
    const term = q.trim().toLowerCase();
    return (data.pending || []).filter((i) =>
      (i.student?.name || "").toLowerCase().includes(term) ||
      (i.student?.display_id || "").toLowerCase().includes(term),
    );
  }, [data, q]);

  return (
    <div className="space-y-3">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center" data-testid="kpi-pending-pickup">
          <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Pending Pickup</p>
          <p className="text-2xl font-bold text-amber-700">{data.pending_count || 0}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center" data-testid="kpi-handed-over">
          <p className="text-[10px] uppercase tracking-wide text-green-700 font-semibold">Handed Over</p>
          <p className="text-2xl font-bold text-green-700">{data.handed_over_count || 0}</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or ID…" className="h-9 rounded-full text-sm flex-1" data-testid="queue-search" />
        <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)} className="rounded-full">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <p className="text-center text-sm text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-center text-sm text-slate-500 py-8" data-testid="queue-empty">No pending pickups.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((i) => (
            <div key={i.attendance_id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3" data-testid={`queue-row-${i.attendance_id}`}>
              <div className="w-10 h-10 rounded-full bg-violet-100 grid place-items-center overflow-hidden shrink-0">
                {i.student?.photo_data ? <img src={`data:image/jpeg;base64,${i.student.photo_data}`} alt="" className="w-full h-full object-cover" /> : <span className="text-sm font-bold text-violet-600">{(i.student?.name || "?").charAt(0).toUpperCase()}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{i.student?.name}</p>
                <p className="text-[11px] text-slate-500 truncate">{i.student?.display_id} · {i.session?.batch_name || "—"} · {formatTime12(i.session?.start_time)}–{formatTime12(i.session?.end_time)}</p>
              </div>
              <Button size="sm" onClick={() => setVerify(i)} className="rounded-full text-xs bg-green-600 hover:bg-green-700 text-white" data-testid={`queue-handover-${i.attendance_id}`}>
                Hand Over
              </Button>
            </div>
          ))}
        </div>
      )}

      {verify && (
        <Dialog open onOpenChange={(o) => !o && setVerify(null)}>
          <DialogContent className="max-w-sm w-[92vw]" data-testid="queue-verify-dialog">
            <DialogHeader><DialogTitle className="text-base font-heading">Confirm Handover</DialogTitle></DialogHeader>
            <VerificationCard
              data={{ student: verify.student, pending_session: verify.session, attendance_id: verify.attendance_id, warning: null }}
              onCleared={() => { setVerify(null); setRefreshKey((k) => k + 1); }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// =================================================================
// Manual Override Tab (Admin only) — full searchable student list,
// session picker, and ad-hoc session-less handover support.
// =================================================================

const ManualOverrideTab = () => {
  const [students, setStudents] = useState([]);
  const [pending, setPending] = useState([]); // for matching attendance_ids
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/pickup/students"),
      api.get("/pickup/queue"),
    ]).then(([sRes, qRes]) => {
      setStudents(sRes.data?.students || []);
      setPending(qRes.data?.pending || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Map student_id -> [pending_attendance_records]
  const pendingByStudent = useMemo(() => {
    const m = {};
    for (const p of pending) {
      const sid = p.student?.user_id;
      if (!sid) continue;
      m[sid] = m[sid] || [];
      m[sid].push(p);
    }
    return m;
  }, [pending]);

  const filtered = useMemo(() => {
    if (!q.trim()) return students;
    const term = q.trim().toLowerCase();
    return students.filter((s) =>
      (s.name || "").toLowerCase().includes(term) ||
      (s.display_id || "").toLowerCase().includes(term) ||
      (s.phone || "").includes(term) ||
      (s.parent_phone || "").includes(term),
    );
  }, [students, q]);

  return (
    <div className="space-y-3" data-testid="manual-override-tab">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex gap-2">
        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Manual Override</p>
          <p>Use this for emergencies (QR unavailable, phone dead, scan failure). Notes are mandatory and stored in the audit log.</p>
        </div>
      </div>

      <Input
        value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name, ID, phone or parent phone…"
        className="h-9 rounded-full text-sm" data-testid="manual-search"
      />

      {loading ? (
        <p className="text-center text-sm text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-center text-sm text-slate-500 py-6">No students match.</p>
      ) : (
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {filtered.map((s) => {
            const pendings = pendingByStudent[s.user_id] || [];
            const inactive = !s.is_active;
            return (
              <button
                key={s.user_id}
                onClick={() => setPicked({ student: s, pendings })}
                className={`w-full text-left bg-white rounded-xl border p-3 hover:border-violet-400 transition flex items-center gap-3 ${inactive ? "opacity-60 border-slate-200" : "border-slate-200"}`}
                data-testid={`manual-pick-${s.user_id}`}
              >
                <div className="w-9 h-9 rounded-full bg-violet-100 grid place-items-center overflow-hidden shrink-0">
                  {s.photo_data ? <img src={`data:image/jpeg;base64,${s.photo_data}`} alt="" className="w-full h-full object-cover" /> : <span className="text-sm font-bold text-violet-600">{(s.name || "?").charAt(0).toUpperCase()}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                    {s.name}
                    {inactive && <span className="px-1.5 py-0 rounded-full text-[9px] font-semibold bg-slate-100 text-slate-500">Inactive</span>}
                    {!s.pickup_authorization_enabled && <span className="px-1.5 py-0 rounded-full text-[9px] font-semibold bg-amber-50 text-amber-600">Auth OFF</span>}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {s.display_id} · {s.phone || s.parent_phone || "—"}
                    {pendings.length > 0 && <span className="text-amber-700 font-semibold ml-1">· {pendings.length} pending</span>}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {picked && (
        <ManualHandoverDialog
          picked={picked}
          onClose={() => setPicked(null)}
          onDone={() => { setPicked(null); refresh(); }}
        />
      )}
    </div>
  );
};

const ManualHandoverDialog = ({ picked, onClose, onDone }) => {
  const { student, pendings } = picked;
  // If multiple pendings → require explicit pick. If one → preselect. If zero → ad-hoc (no attendance_id).
  const [sessionPickerIdx, setSessionPickerIdx] = useState(pendings.length > 0 ? 0 : -1);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!notes.trim()) { toast.error("Notes are required for manual override"); return; }
    setBusy(true);
    try {
      const payload = {
        student_id: student.user_id,
        notes: notes.trim(),
      };
      if (sessionPickerIdx >= 0 && pendings[sessionPickerIdx]) {
        payload.attendance_id = pendings[sessionPickerIdx].attendance_id;
      }
      await api.post("/pickup/manual-handover", payload);
      toast.success(`Manual handover recorded for ${student.name}`);
      playDing();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md w-[92vw]" data-testid="manual-handover-dialog">
        <DialogHeader><DialogTitle className="text-base font-heading">Manual Handover</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl bg-slate-50 border p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-100 grid place-items-center overflow-hidden shrink-0">
              {student.photo_data ? <img src={`data:image/jpeg;base64,${student.photo_data}`} alt="" className="w-full h-full object-cover" /> : <span className="text-sm font-bold text-violet-600">{(student.name || "?").charAt(0).toUpperCase()}</span>}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-900">{student.name}</p>
              <p className="text-[11px] text-violet-700">{student.display_id || "—"}</p>
            </div>
          </div>

          {/* Session picker */}
          {pendings.length > 0 ? (
            <div className="space-y-1">
              <Label className="text-xs">Which session?</Label>
              <div className="space-y-1.5">
                {pendings.map((p, idx) => (
                  <label key={p.attendance_id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs ${sessionPickerIdx === idx ? "border-violet-500 bg-violet-50" : "border-slate-200"}`}>
                    <input
                      type="radio"
                      checked={sessionPickerIdx === idx}
                      onChange={() => setSessionPickerIdx(idx)}
                      data-testid={`session-pick-${idx}`}
                    />
                    <span className="flex-1">
                      <span className="font-semibold text-slate-900">{p.session?.batch_name || "—"}</span>
                      <span className="text-slate-500"> · {formatTime12(p.session?.start_time)}–{formatTime12(p.session?.end_time)}</span>
                    </span>
                  </label>
                ))}
                <label className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs ${sessionPickerIdx === -1 ? "border-violet-500 bg-violet-50" : "border-slate-200"}`}>
                  <input type="radio" checked={sessionPickerIdx === -1} onChange={() => setSessionPickerIdx(-1)} data-testid="session-pick-adhoc" />
                  <span className="text-slate-700">Ad-hoc handover (no session link)</span>
                </label>
              </div>
            </div>
          ) : (
            <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs p-2.5 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>No Present session for this student today. This will record an <strong>ad-hoc handover</strong> (no session link) — strongly recommend marking attendance first if possible.</span>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Reason / Notes <span className="text-red-500">*</span></Label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. QR phone unavailable; parent identified by ID and verbal confirmation."
              className="rounded-xl text-sm"
              data-testid="manual-notes"
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="w-full sm:flex-1 rounded-full" disabled={busy}>Cancel</Button>
            <Button onClick={submit} className="w-full sm:flex-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white" disabled={busy} data-testid="manual-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Record Handover
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =================================================================
// Logs Tab
// =================================================================

const LogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { default_timezone } = useBrand();

  useEffect(() => {
    let cancelled = false;
    api.get("/pickup/logs").then((r) => { if (!cancelled) setLogs(r.data?.logs || []); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="text-center text-sm text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</p>;
  if (!logs.length) return <p className="text-center text-sm text-slate-500 py-8" data-testid="logs-empty">No pickup logs.</p>;

  return (
    <div className="space-y-2" data-testid="logs-tab">
      {logs.map((l) => (
        <div key={l.log_id} className="bg-white rounded-xl border border-slate-200 p-3 text-sm">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-semibold text-slate-900">{l.student_name} <span className="text-violet-700 text-xs ml-1">{l.display_id}</span></p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${l.method === "manual_override" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
              {l.method === "manual_override" ? "MANUAL" : l.method === "qr_scan" ? "QR SCAN" : (l.method || "—").toUpperCase()}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1" data-testid={`log-ts-${l.log_id}`}>
            {formatDateTime(l.timestamp, default_timezone)} · {l.batch_name || "Ad-hoc"} · {l.studio_name || "—"}
          </p>
          <p className="text-[11px] text-slate-500">By {l.verified_by_name} ({l.verified_by_role})</p>
          {l.notes && <p className="text-[11px] text-slate-600 mt-1 italic">"{l.notes}"</p>}
        </div>
      ))}
    </div>
  );
};
